import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AgentSandbox.css';
import LivePreview from './LivePreview.js';
import GmailPreviewPanel from './GmailPreviewPanel.js';
import IdeaGenerationPreview from './IdeaGenerationPreview.js';
import PromptPresets from './PromptPresets.js';
import SandboxBrowser from './SandboxBrowser.js';
import { createZipAndDownload } from '../utils/exportZip.js';
import { filesFromSource, previewFromFiles } from '../siteSystem.js';

function truncate(str, max) {
  const s = String(str || '');
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 3))}...`;
}

function isLikelyVideoUrl(url) {
  const u = String(url || '').split('?')[0].toLowerCase();
  return /\.(mp4|webm|ogg|mov|m4v)$/i.test(u) || /\/video\//i.test(u) || /mime=video/i.test(String(url || ''));
}

const EMPTY_SECONDS = 0;

function formatClock(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  const total = Math.floor(safe);
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hrs > 0) return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function trimGap(length) {
  const span = Math.max(0.2, Number(length) || 1);
  return Math.min(0.4, Math.max(0.1, span / 5));
}

function ComputerVideoView({ edit = null, active = true }) {
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const fileRef = useRef(null);
  const dragRef = useRef('');
  const editRef = useRef(edit);
  const trimRef = useRef({ start: 0, end: EMPTY_SECONDS, current: 0, duration: EMPTY_SECONDS });
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const appliedTrimId = useRef(null);
  const pendingEditRef = useRef(null);
  const [media, setMedia] = useState(null);
  const [duration, setDuration] = useState(EMPTY_SECONDS);
  const [current, setCurrent] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(EMPTY_SECONDS);
  const [playing, setPlaying] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [dropNote, setDropNote] = useState('');
  editRef.current = edit;
  trimRef.current = { start: trimStart, end: trimEnd, current, duration };

  useEffect(() => {
    return () => {
      if (media?.url) URL.revokeObjectURL(media.url);
    };
  }, [media]);

  const remember = () => {
    undoRef.current.push({ ...trimRef.current });
    if (undoRef.current.length > 30) undoRef.current.shift();
    redoRef.current = [];
  };

  const clearHistory = () => {
    undoRef.current = [];
    redoRef.current = [];
  };

  const commitTrim = (start, end, note, playhead) => {
    const length = trimRef.current.duration > 0 ? trimRef.current.duration : 0;
    if (length <= 0) {
      if (note) setDropNote(note);
      return;
    }
    const gap = trimGap(length);
    const nextStart = Math.max(0, Math.min(Number(start) || 0, length - gap));
    const nextEnd = Math.min(length, Math.max(Number(end) || 0, nextStart + gap));
    const head = playhead == null
      ? Math.min(nextEnd, Math.max(nextStart, trimRef.current.current))
      : Math.min(length, Math.max(0, playhead));
    trimRef.current = { ...trimRef.current, start: nextStart, end: nextEnd, current: head, duration: length };
    setTrimStart(nextStart);
    setTrimEnd(nextEnd);
    setCurrent(head);
    if (videoRef.current) videoRef.current.currentTime = head;
    if (note) setDropNote(note);
  };

  const applyTrimRequest = (request, length, record) => {
    const span = Number(length) || 0;
    if (span <= 0) {
      pendingEditRef.current = request;
      setDropNote('Open a video first, then that trim can be applied.');
      return;
    }
    const gap = trimGap(span);
    let start = Number(request.start) || 0;
    let end = request.fromEnd != null
      ? span - Number(request.fromEnd)
      : (request.end == null ? span : Number(request.end));
    start = Math.max(0, Math.min(start, span - gap));
    end = Math.min(span, Math.max(end, start + gap));
    if (record && appliedTrimId.current !== request.id) {
      remember();
      appliedTrimId.current = request.id;
    }
    pendingEditRef.current = null;
    trimRef.current = { ...trimRef.current, duration: span };
    commitTrim(start, end, `Keeping ${formatClock(start)} to ${formatClock(end)}.`, start);
    setPlaying(Boolean(request.play));
  };

  useEffect(() => {
    if (!active) setPlaying(false);
  }, [active]);

  useEffect(() => {
    if (!edit?.id) return;
    if (edit.action === 'open' || edit.needMedia) {
      if (!media) {
        setDropNote('Open or drop a video to start editing.');
        const timer = window.setTimeout(() => fileRef.current?.click(), 180);
        return () => window.clearTimeout(timer);
      }
      setDropNote(media.name || 'Clip loaded.');
      return undefined;
    }
    if (edit.action === 'play') {
      if (!media || trimRef.current.duration <= 0) {
        setDropNote('Open a video first, then play.');
        pendingEditRef.current = edit;
        const timer = window.setTimeout(() => fileRef.current?.click(), 180);
        return () => window.clearTimeout(timer);
      }
      const start = trimRef.current.start;
      const end = trimRef.current.end;
      setCurrent((value) => (value < start || value >= end - 0.05 ? start : value));
      if (videoRef.current) {
        const video = videoRef.current;
        if (video.currentTime < start || video.currentTime >= end - 0.05) video.currentTime = start;
      }
      setPlaying(true);
      setDropNote('Playing the part you are keeping.');
      return undefined;
    }
    if (edit.action !== 'trim') return undefined;
    if (!media || trimRef.current.duration <= 0) {
      pendingEditRef.current = edit;
      setDropNote('Open a video first, then that trim can be applied.');
      const timer = window.setTimeout(() => fileRef.current?.click(), 180);
      return () => window.clearTimeout(timer);
    }
    applyTrimRequest(edit, trimRef.current.duration, true);
    return undefined;
  }, [edit, media]);

  useEffect(() => {
    if (playing && duration > 0 && current >= trimEnd - 0.001) setPlaying(false);
  }, [playing, current, trimEnd, duration]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !media) return undefined;
    if (!playing) {
      video.pause();
      return undefined;
    }
    if (video.currentTime < trimStart || video.currentTime >= trimEnd - 0.05) {
      video.currentTime = trimStart;
    }
    video.play().catch(() => setPlaying(false));
    return undefined;
  }, [playing, media, trimStart, trimEnd]);

  const ratioFromClientX = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };

  const applyDrag = (which, clientX) => {
    if (duration <= 0) return;
    const time = ratioFromClientX(clientX) * duration;
    const gap = Math.min(0.4, Math.max(0.1, duration / 5));
    if (which === 'start') {
      const nextStart = Math.max(0, Math.min(time, trimEnd - gap));
      setTrimStart(nextStart);
      if (current < nextStart) {
        setCurrent(nextStart);
        if (videoRef.current) videoRef.current.currentTime = nextStart;
      }
      return;
    }
    if (which === 'end') {
      const nextEnd = Math.min(duration, Math.max(time, trimStart + gap));
      setTrimEnd(nextEnd);
      if (current > nextEnd) {
        setCurrent(nextEnd);
        if (videoRef.current) videoRef.current.currentTime = nextEnd;
      }
      return;
    }
    const next = Math.min(trimEnd, Math.max(trimStart, time));
    setCurrent(next);
    if (videoRef.current) videoRef.current.currentTime = next;
  };

  const beginDrag = (which) => (event) => {
    if (duration <= 0) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = which;
    event.currentTarget.setPointerCapture(event.pointerId);
    applyDrag(which, event.clientX);
  };

  const moveDrag = (event) => {
    if (!dragRef.current) return;
    applyDrag(dragRef.current, event.clientX);
  };

  const endDrag = (event) => {
    dragRef.current = '';
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const togglePlay = () => {
    if (!media || duration <= 0) {
      setDropNote('Open or drop a video to play.');
      fileRef.current?.click();
      return;
    }
    setPlaying((on) => {
      if (on) return false;
      if (current < trimStart || current >= trimEnd - 0.05) {
        setCurrent(trimStart);
        if (videoRef.current) videoRef.current.currentTime = trimStart;
      }
      return true;
    });
  };

  const clearMedia = () => {
    setPlaying(false);
    setMedia((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setDuration(EMPTY_SECONDS);
    setCurrent(0);
    setTrimStart(0);
    setTrimEnd(EMPTY_SECONDS);
    clearHistory();
    pendingEditRef.current = null;
    setDropNote('Open or drop a video to start editing.');
  };

  const takeFile = (file) => {
    if (!file) return;
    const looksVideo = file.type.startsWith('video/') || isLikelyVideoUrl(file.name);
    if (!looksVideo) {
      setDropNote('Choose a video file (mp4, webm, mov…).');
      return;
    }
    const url = URL.createObjectURL(file);
    setPlaying(false);
    setCurrent(0);
    setTrimStart(0);
    setTrimEnd(EMPTY_SECONDS);
    setDuration(EMPTY_SECONDS);
    clearHistory();
    setMedia((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return { url, name: file.name || 'Video' };
    });
    setDropNote(file.name || 'Clip loaded.');
  };

  const onFileVideoTime = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime >= trimEnd) {
      video.pause();
      video.currentTime = trimEnd;
      setCurrent(trimEnd);
      setPlaying(false);
      return;
    }
    if (video.currentTime < trimStart) {
      video.currentTime = trimStart;
      setCurrent(trimStart);
      return;
    }
    setCurrent(video.currentTime || 0);
  };

  const startPct = duration ? (trimStart / duration) * 100 : 0;
  const endPct = duration ? (trimEnd / duration) * 100 : 0;
  const playPct = duration ? (current / duration) * 100 : 0;
  const hasClip = Boolean(media && duration > 0);

  return (
    <div
      className={`computer-video${dropping ? ' is-dropping' : ''}${hasClip ? ' has-clip' : ' is-empty'}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDropping(true);
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDropping(false);
        takeFile(event.dataTransfer?.files?.[0]);
      }}
    >
      <div className="computer-video-player">
        {media ? (
          <video
            ref={videoRef}
            src={media.url}
            playsInline
            preload="metadata"
            className="computer-video-media"
            onLoadedMetadata={(event) => {
              const next = Number(event.currentTarget.duration);
              const safe = Number.isFinite(next) && next > 0 ? next : 0;
              setDuration(safe);
              setTrimStart(0);
              setTrimEnd(safe);
              setCurrent(0);
              trimRef.current = { start: 0, end: safe, current: 0, duration: safe };
              const pending = pendingEditRef.current;
              if (pending?.action === 'trim') {
                applyTrimRequest(pending, safe, true);
              } else if (pending?.action === 'play') {
                pendingEditRef.current = null;
                setPlaying(true);
                setDropNote('Playing the part you are keeping.');
              }
            }}
            onTimeUpdate={onFileVideoTime}
            onEnded={() => setPlaying(false)}
          />
        ) : (
          <div className="computer-video-empty">
            <span className="computer-video-empty-kicker">Video</span>
            <p className="computer-video-empty-title">Open a clip to preview</p>
            <p className="computer-video-empty-hint">
              Drop a file here or use Open. Preview, scrub, and trim stay on this panel.
            </p>
            <button type="button" className="computer-video-empty-btn" onClick={() => fileRef.current?.click()}>
              Open video
            </button>
          </div>
        )}
        {media && !playing ? (
          <button type="button" className="computer-video-stage-play" aria-label="Play" onClick={togglePlay}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5.5v13l11-6.5-11-6.5z" />
            </svg>
          </button>
        ) : null}
        {dropping ? <div className="computer-video-drop">Drop video</div> : null}
      </div>
      <div className="computer-video-dock">
        <div className="computer-video-controls">
          <button
            type="button"
            className="computer-video-play"
            aria-label={playing ? 'Pause' : 'Play'}
            onClick={togglePlay}
            disabled={!hasClip}
          >
            {playing ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M7 5h4v14H7V5zm6 0h4v14h-4V5z" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5.5v13l11-6.5-11-6.5z" />
              </svg>
            )}
          </button>
          <span className="computer-video-time">
            {formatClock(current)} / {formatClock(duration)}
          </span>
          <span className="computer-video-trim">
            {'Trim '}{formatClock(trimStart)}{' \u2013 '}{formatClock(trimEnd)}
          </span>
          <button type="button" className="computer-video-textbtn" onClick={() => fileRef.current?.click()}>
            Open
          </button>
          {media ? (
            <button type="button" className="computer-video-textbtn" onClick={clearMedia}>
              Clear
            </button>
          ) : null}
          <input
            ref={fileRef}
            type="file"
            accept="video/*"
            hidden
            onChange={(event) => {
              takeFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </div>
        <div
          className={`computer-video-track${hasClip ? '' : ' is-disabled'}`}
          ref={trackRef}
          role="slider"
          aria-label="Timeline"
          aria-disabled={!hasClip}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(current)}
          onPointerDown={(event) => {
            if (!hasClip) return;
            if (event.target !== event.currentTarget && event.target.closest('[data-handle]')) return;
            beginDrag('seek')(event);
          }}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {hasClip ? (
            <div
              className="computer-video-clip"
              style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
              title={media?.name || 'Clip'}
            >
              <span className="computer-video-clip-label">{media?.name || 'Clip'}</span>
            </div>
          ) : (
            <div className="computer-video-track-empty">No clip loaded</div>
          )}
          {hasClip ? <div className="computer-video-range" style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }} /> : null}
          {hasClip ? <div className="computer-video-playhead" style={{ left: `${playPct}%` }} /> : null}
          {hasClip ? (
            <>
              <button
                type="button"
                className="computer-video-handle is-start"
                data-handle="start"
                aria-label="Trim start"
                style={{ left: `${startPct}%` }}
                onPointerDown={beginDrag('start')}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              />
              <button
                type="button"
                className="computer-video-handle is-end"
                data-handle="end"
                aria-label="Trim end"
                style={{ left: `${endPct}%` }}
                onPointerDown={beginDrag('end')}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              />
            </>
          ) : null}
        </div>
        <p className="computer-video-hint">
          {dropNote || (media ? media.name : 'Open or drop a video to start editing.')}
        </p>
      </div>
    </div>
  );
}

function fileTone(path) {
  const ext = String(path || '').split('.').pop().toLowerCase();
  if (ext === 'html') return '#e37933';
  if (ext === 'css') return '#519aba';
  if (ext === 'js') return '#cbcb41';
  if (ext === 'json') return '#a8cc7c';
  return '#c5c5c5';
}

function FileGlyph({ path }) {
  return (
    <svg className="live-file-glyph" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path fill={fileTone(path)} d="M3.2 1.2h6.05L13 4.95V14.8H3.2V1.2z" />
      <path fill="#1e1e1e" d="M9.05 1.25V5.1H12.9" />
    </svg>
  );
}

function CodePane({ files, activePath, onSelect, text, caret, note }) {
  const scrollRef = useRef(null);
  const lines = String(text || '').split('\n');

  useEffect(() => {
    const node = scrollRef.current;
    if (!node || !caret) return;
    node.scrollTop = node.scrollHeight;
  }, [text, caret]);

  return (
    <>
      {note ? <p className="live-file-note">{note}</p> : null}
      <div className="live-ide">
        <aside className="live-explorer" aria-label="Explorer">
          <div className="live-explorer-label">Explorer</div>
          <div className="live-tree-root">
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
              <path fill="none" stroke="#52525b" strokeWidth="1.4" d="M4 6.5 8 10.5 12 6.5" />
            </svg>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path fill="#dcb67a" d="M1.4 3.4h4.8l1.3 1.5h7.1v7.7H1.4V3.4z" />
            </svg>
            <span>site</span>
          </div>
          <div className="live-explorer-files" role="tree">
            {files.map((file) => {
              const open = file.path === activePath;
              const dirty = String(file.shown || '').length < String(file.content || '').length;
              return (
                <button
                  key={file.path}
                  type="button"
                  role="treeitem"
                  aria-selected={open}
                  className={`live-file${open ? ' is-on' : ''}`}
                  onClick={() => onSelect(file.path)}
                >
                  <FileGlyph path={file.path} />
                  <span className="live-file-name">{file.path}</span>
                  {dirty ? <span className="live-file-dirty" aria-label="Writing" /> : null}
                </button>
              );
            })}
          </div>
        </aside>
        <div className="live-editor">
          <div className="live-tabs" role="tablist" aria-label="Open files">
            <div className="live-tab is-on" role="tab" aria-selected="true">
              <FileGlyph path={activePath} />
              <span>{activePath}</span>
            </div>
          </div>
          <div className="live-code-scroll" ref={scrollRef}>
            <div className="live-gutter" aria-hidden="true">
              {lines.map((_, index) => (
                <span key={index}>{index + 1}</span>
              ))}
            </div>
            <pre>
              {text}
              {caret ? <span className="live-caret" /> : null}
            </pre>
          </div>
        </div>
      </div>
    </>
  );
}

function LiveSiteScreen({ siteBuild, solo = 'code' }) {
  const files = Array.isArray(siteBuild?.files) ? siteBuild.files : [];
  const buildStamp = files.find((file) => file.path === 'system.json')?.content || '';
  const [pinnedPath, setPinnedPath] = useState(null);
  const [seenStamp, setSeenStamp] = useState(buildStamp);

  if (buildStamp !== seenStamp) {
    setSeenStamp(buildStamp);
    setPinnedPath(null);
  }

  const pinned = pinnedPath ? files.find((file) => file.path === pinnedPath) : null;
  const active = pinned || files.find((file) => file.path === siteBuild?.active) || files[0];
  const preview = siteBuild?.preview || { html: '', css: '', js: '' };
  const phase = siteBuild?.phase || (siteBuild?.streaming ? 'code' : 'done');
  const writing = phase !== 'done';
  const thoughts = Array.isArray(siteBuild?.thoughts) ? siteBuild.thoughts : [];
  const stillWriting = phase === 'code' && Boolean(active && String(active.shown || '').length < String(active.content || '').length);

  if (phase === 'thinking') {
    return (
      <div className="live-screen is-writing">
        <div className="live-thoughts" aria-label="Thinking">
          {thoughts.filter((thought) => thought.shown).map((thought) => (
            <p key={thought.text} className="live-thought">
              {thought.shown}
              {thought.shown.length < thought.text.length ? <span className="live-caret" /> : null}
            </p>
          ))}
        </div>
      </div>
    );
  }

  if (!active) return null;

  if (!writing && solo === 'preview') {
    return (
      <div className="live-screen">
        <section className="live-page" aria-label="Live page">
          <LivePreview html={preview.html} css={preview.css} js={preview.js} isVisible hideChrome />
        </section>
      </div>
    );
  }

  return (
    <div className="live-screen is-writing">
      <div className="live-screen-split">
        <section className="live-code" aria-label="Code">
          <CodePane
            files={files}
            activePath={active.path}
            onSelect={setPinnedPath}
            text={active.shown || ''}
            caret={stillWriting}
            note={siteBuild?.fileNote || ''}
          />
        </section>
      </div>
    </div>
  );
}

function CodeExplorer({ siteBuild }) {
  const [picked, setPicked] = useState('system.json');
  if (siteBuild?.files?.length) return <LiveSiteScreen siteBuild={siteBuild} solo="code" />;
  const names = ['system.json', 'README.md', 'package.json', '.gitignore', 'site.config.json', 'styles.css', 'app.js', 'index.html'];
  return (
    <div className="live-screen is-writing">
      <div className="live-screen-split">
        <section className="live-code" aria-label="Code">
          <CodePane
            files={names.map((path) => ({ path, shown: '', content: '' }))}
            activePath={picked}
            onSelect={setPicked}
            text=""
          />
        </section>
      </div>
    </div>
  );
}

const SETTINGS_NAV = [
  ['general', 'General'],
  ['notification', 'Notification'],
  ['seo', 'SEO'],
  ['hosting', 'Hosting'],
  ['domains', 'Domains'],
  ['secrets', 'Secrets'],
  ['integrations', 'Integrations'],
  ['github', 'GitHub'],
  ['usage', 'Usage'],
];

function SettingsSwitch({ label, detail, checked, onChange }) {
  return (
    <label className="computer-settings-switch">
      <span>
        <strong>{label}</strong>
        {detail ? <span>{detail}</span> : null}
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}

function ComputerSettings({
  section,
  onSection,
  form,
  setField,
  onPublish,
  onCopy,
  onAsk,
  sessionBuilds,
  onOpenChatUsage,
}) {
  const panel = () => {
    if (section === 'notification') {
      return (
        <>
          <h2>Notification</h2>
          <SettingsSwitch label="Email when the build finishes" checked={form.emailOnFinish} onChange={(value) => setField('emailOnFinish', value)} />
          <SettingsSwitch label="Browser alerts" checked={form.browserAlerts} onChange={(value) => setField('browserAlerts', value)} />
        </>
      );
    }
    if (section === 'seo') {
      return (
        <>
          <h2>SEO</h2>
          <label className="computer-settings-field">
            Title
            <input value={form.seoTitle} onChange={(event) => setField('seoTitle', event.target.value)} />
          </label>
          <label className="computer-settings-field">
            Meta description
            <textarea value={form.seoDescription} rows={3} onChange={(event) => setField('seoDescription', event.target.value)} />
          </label>
        </>
      );
    }
    if (section === 'hosting') {
      return (
        <>
          <h2>Hosting</h2>
          <p>Manage how your website runs once published.</p>
          <h3>Deployment</h3>
          <h4>Static website</h4>
          <p>Your pages are built ahead of time and delivered as ready-made files. This is fast and works well for landing pages, portfolios, and sites updated through new publishes.</p>
          <h4>Need server features?</h4>
          <p>For secure forms, sign-in, private data, or custom APIs.</p>
          <button type="button" className="computer-settings-action" onClick={() => onAsk('Ask in the chat if you need server features.')}>Ask Zolaid</button>
          <h3>Publishing preferences</h3>
          <SettingsSwitch
            label="Auto Publish"
            detail="Automatically publish when a new checkpoint is saved."
            checked={form.autoPublish}
            onChange={(value) => setField('autoPublish', value)}
          />
          <SettingsSwitch
            label="Agent Publish"
            detail="Allow Zolaid to publish without asking each time."
            checked={form.agentPublish}
            onChange={(value) => setField('agentPublish', value)}
          />
        </>
      );
    }
    if (section === 'domains') {
      return (
        <>
          <h2>Domains</h2>
          <label className="computer-settings-field">
            Domain
            <input value={form.domainName} placeholder="example.com" onChange={(event) => setField('domainName', event.target.value)} />
          </label>
        </>
      );
    }
    if (section === 'secrets') {
      return (
        <>
          <h2>Secrets</h2>
          <label className="computer-settings-field">
            Name
            <input type="password" value={form.secretName} autoComplete="off" placeholder="Name" onChange={(event) => setField('secretName', event.target.value)} />
          </label>
        </>
      );
    }
    if (section === 'integrations') {
      return (
        <>
          <h2>Integrations</h2>
          <ul className="computer-settings-list">
            <li>Google Drive</li>
            <li>OneDrive</li>
            <li>Gmail</li>
          </ul>
        </>
      );
    }
    if (section === 'github') {
      return (
        <>
          <h2>GitHub</h2>
          <p>{'Use GitHub as your project\u2019s repository and edit your code inside or outside Zolaid.'}</p>
          <p className="computer-settings-status">Not connected</p>
          <p>Let Zolaid help you connect GitHub. You will confirm the repository before any code is transferred.</p>
          <button type="button" className="computer-settings-action" onClick={() => onAsk('Ask in the chat when you want Zolaid to connect GitHub.')}>Ask Zolaid to connect</button>
        </>
      );
    }
    if (section === 'usage') {
      return (
        <>
          <h2>Usage</h2>
          <p>{sessionBuilds ? `${sessionBuilds} build${sessionBuilds === 1 ? '' : 's'} this session.` : 'No builds this session.'}</p>
        </>
      );
    }
    return (
      <>
        <h2>General</h2>
        <h3>Publish & Access</h3>
        <p className="computer-settings-status">Not published</p>
        <p>Your website has not been publicly launched yet and can only be accessed by you through the preview link.</p>
        <h3>Hosting</h3>
        <p>Manage your website's hosting and application server.</p>
        <button type="button" className="computer-settings-action" onClick={() => onSection('hosting')}>Manage hosting</button>
        <h3>Website actions</h3>
        <h4>Duplicate website</h4>
        <p>Create an independent copy of this website in a new session.</p>
        <button type="button" className="computer-settings-action" onClick={onCopy}>Make a copy</button>
        <h3>Advanced</h3>
        <SettingsSwitch
          label="Hide Zolaid badge"
          detail={'Remove "Made with Zolaid" badge from your website.'}
          checked={form.hideBadge}
          onChange={(value) => setField('hideBadge', value)}
        />
      </>
    );
  };

  return (
    <div className="computer-settings">
      <aside className="computer-settings-nav" aria-label="Setting">
        {SETTINGS_NAV.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={section === id ? 'is-on' : ''}
            aria-current={section === id ? 'page' : undefined}
            onClick={() => {
              if (id === 'usage' && typeof onOpenChatUsage === 'function') {
                onOpenChatUsage();
                return;
              }
              onSection(id);
            }}
          >
            {label}
          </button>
        ))}
      </aside>
      <section className="computer-settings-main" aria-label={SETTINGS_NAV.find(([id]) => id === section)?.[1] || 'General'}>
        <div className="computer-settings-toolbar">
          <button type="button" className="computer-publish-btn" onClick={onPublish}>
            <span aria-hidden="true">{'\uD83D\uDE80'}</span>
            Publish
          </button>
        </div>
        {panel()}
      </section>
    </div>
  );
}

const SLIDE_LAYOUTS = ['title', 'agenda', 'points', 'stat', 'quote', 'columns', 'steps', 'close'];

function slideLine(value) {
  return String(value || '').trim();
}

function slideLayout(slide, index) {
  const named = slideLine(slide?.layout).toLowerCase();
  if (SLIDE_LAYOUTS.includes(named)) return named;
  if (index === 0) return 'title';
  if (slideLine(slide?.quote)) return 'quote';
  if (slideLine(slide?.stat)) return 'stat';
  if (Array.isArray(slide?.points) && slide.points.length) return 'points';
  if (Array.isArray(slide?.items) && slide.items.length) return 'agenda';
  if (Array.isArray(slide?.columns) && slide.columns.length) return 'columns';
  if (Array.isArray(slide?.steps) && slide.steps.length) return 'steps';
  if (slideLine(slide?.cta)) return 'close';
  return 'body';
}

function slideHeading(slide, index) {
  return slideLine(slide?.title) || slideLine(slide?.quote) || slideLine(slide?.kicker) || `Slide ${index + 1}`;
}

function SlideFace({ slide, index, total }) {
  const layout = slideLayout(slide, index);
  const kicker = slideLine(slide?.kicker);
  const title = slideLine(slide?.title);
  const heading = slideHeading(slide, index);
  const page = String(index + 1).padStart(2, '0');
  const items = Array.isArray(slide?.items) ? slide.items.map(slideLine).filter(Boolean) : [];
  const points = Array.isArray(slide?.points) ? slide.points.filter(Boolean) : [];
  const columns = Array.isArray(slide?.columns) ? slide.columns.filter(Boolean) : [];
  const steps = Array.isArray(slide?.steps) ? slide.steps.filter(Boolean) : [];
  const lead = slideLine(slide?.subtitle) || (layout === 'body' || layout === 'close' ? slideLine(slide?.body) : '');

  return (
    <article className={`computer-slide is-${layout}`} aria-label={`Slide ${index + 1} of ${total}`}>
      <div className="computer-slide-shape" aria-hidden="true" />
      <div className="computer-slide-band" aria-hidden="true" />
      {(layout === 'title' || layout === 'close') ? <div className="computer-slide-gold" aria-hidden="true" /> : null}
      <div className="computer-slide-main">
        {kicker ? <span className="computer-slide-kicker">{kicker}</span> : null}
        {layout === 'stat' ? (
          <div className="computer-slide-stat">
            <b>{slideLine(slide?.stat) || heading}</b>
            {slideLine(slide?.statLabel) ? <span>{slide.statLabel}</span> : null}
          </div>
        ) : null}
        {layout === 'quote' ? (
          <blockquote className="computer-slide-quote">{slideLine(slide?.quote) || heading}</blockquote>
        ) : (
          <h2>{title || heading}</h2>
        )}
        {lead && layout !== 'quote' ? <p className={layout === 'title' ? 'computer-slide-lead' : 'computer-slide-copy'}>{lead}</p> : null}
        {layout === 'quote' && slideLine(slide?.attribution) ? <p className="computer-slide-attr">{slide.attribution}</p> : null}
        {layout === 'agenda' && items.length ? (
          <ol className="computer-slide-agenda">
            {items.map((item, itemIndex) => (
              <li key={`${item}-${itemIndex}`}>
                <span>{String(itemIndex + 1).padStart(2, '0')}</span>
                {item}
              </li>
            ))}
          </ol>
        ) : null}
        {layout === 'points' && points.length ? (
          <div className="computer-slide-points">
            {points.map((point, pointIndex) => (
              <div className="computer-slide-point" key={`${point.title || 'point'}-${pointIndex}`}>
                <em>{String(pointIndex + 1).padStart(2, '0')}</em>
                <strong>{slideLine(point.title) || `Point ${pointIndex + 1}`}</strong>
                {slideLine(point.body) ? <p>{point.body}</p> : null}
              </div>
            ))}
          </div>
        ) : null}
        {layout === 'columns' && columns.length ? (
          <div className="computer-slide-columns">
            {columns.map((column, columnIndex) => (
              <div className="computer-slide-col" key={`${column.title || 'col'}-${columnIndex}`}>
                <strong>{slideLine(column.title) || `Column ${columnIndex + 1}`}</strong>
                {slideLine(column.body) ? <p>{column.body}</p> : null}
              </div>
            ))}
          </div>
        ) : null}
        {layout === 'steps' && steps.length ? (
          <div className="computer-slide-steps">
            {steps.map((step, stepIndex) => (
              <div className="computer-slide-step" key={`${step.title || 'step'}-${stepIndex}`}>
                <em>{String(stepIndex + 1).padStart(2, '0')}</em>
                <strong>{slideLine(step.title) || `Step ${stepIndex + 1}`}</strong>
                {slideLine(step.body) ? <p>{step.body}</p> : null}
              </div>
            ))}
          </div>
        ) : null}
        {layout === 'stat' && slideLine(slide?.body) ? <p className="computer-slide-copy">{slide.body}</p> : null}
        {layout === 'close' && slideLine(slide?.cta) ? <p className="computer-slide-cta">{slide.cta}</p> : null}
        {layout === 'body' && !lead && slideLine(slide?.body) ? <p className="computer-slide-copy">{slide.body}</p> : null}
      </div>
      <span className="computer-slide-page">{page}</span>
    </article>
  );
}

function SlideDeck({ slides, accent, gold, index, onIndex }) {
  const provided = Array.isArray(slides) ? slides.filter(Boolean) : [];
  const list = provided.length
    ? provided
    : [{
      layout: 'title',
      kicker: 'Zolaid Cloud',
      title: 'Launching Zolaid Cloud',
      subtitle: 'The computer writes the work while the room is still in the meeting.',
    }];
  const safe = Math.min(Math.max(Number(index) || 0, 0), list.length - 1);
  const current = list[safe] || list[0];
  const style = {
    '--slide-accent': accent || '#0c3d34',
    '--slide-gold': gold || '#e7c99a',
    '--slide-paper': '#f6f1e8',
    '--slide-ink': '#1c1917',
  };

  return (
    <div className="computer-slides" style={style}>
      <SlideFace key={`${slideHeading(current, safe)}-${safe}`} slide={current} index={safe} total={list.length} />
      <div className="computer-slide-strip" aria-label="Slides">
        {list.map((slide, slideIndex) => (
          <button
            key={`${slideLayout(slide, slideIndex)}-${slideHeading(slide, slideIndex)}-${slideIndex}`}
            type="button"
            className={`computer-slide-thumb${slideIndex === safe ? ' is-current' : ''}`}
            aria-label={`Slide ${slideIndex + 1}: ${slideHeading(slide, slideIndex)}`}
            aria-current={slideIndex === safe ? 'true' : undefined}
            onClick={() => onIndex(slideIndex)}
          >
            <span>{slideLine(slide.kicker) || String(slideIndex + 1).padStart(2, '0')}</span>
            <strong>{slideHeading(slide, slideIndex)}</strong>
          </button>
        ))}
      </div>
    </div>
  );
}

const KIND_LABELS = {
  idle: 'Ready',
  site: 'Live site',
  image: 'Image',
  video: 'Video',
  browser: 'Browser',
  slides: 'Slides',
  build: 'Live',
  gmail: 'Gmail',
  ideas: 'Ideas',
  presets: 'Presets',
  camera: 'Camera',
};

/**
 * The single Preview panel beside chat for every tool:
 * sites, images, video, browser, Gmail, ideas, presets, camera.
 */
export default function AgentSandbox({
  minimized = false,
  size = 'default',
  fullscreen = false,
  onMinimize,
  onToggleFullscreen,
  onClose,
  previewKind = 'idle',
  processLog = [],
  resultImageUrl = null,
  sitePreview = null,
  browserPreviewUrl,
  browserOperatorLiveState,
  slides = [],
  siteBuild = null,
  onComputerRequestFix,
  gmail = null,
  ideaGeneration = null,
  promptPresets = null,
  camera = null,
  videoEdit = null,
  settingsRequest = null,
  onOpenChatUsage = null,
}) {
  const isFull = size === 'full';
  const browserRef = useRef(null);
  const menuWrapRef = useRef(null);
  const moreWrapRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const [addressDraft, setAddressDraft] = useState('');
  const [slideIndex, setSlideIndex] = useState(0);
  const [screenMode, setScreenMode] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [manualBrowserUrl, setManualBrowserUrl] = useState('');
  const [siteCopy, setSiteCopy] = useState(null);
  const [notice, setNotice] = useState('');
  const [settingsSection, setSettingsSection] = useState('general');
  const [sessionBuilds, setSessionBuilds] = useState(0);
  const [liveViewKey, setLiveViewKey] = useState(0);
  const [settingsForm, setSettingsForm] = useState({
    hideBadge: false,
    emailOnFinish: false,
    browserAlerts: false,
    seoTitle: '',
    seoDescription: '',
    domainName: '',
    secretName: '',
    autoPublish: false,
    agentPublish: false,
  });

  const hasBrowserShot = Boolean(browserOperatorLiveState?.screenshotBase64);
  const hasImage = Boolean(resultImageUrl);
  const previewHtml = String(sitePreview?.html || '').trim();
  const previewCss = String(sitePreview?.css || '').trim();
  const previewJs = String(sitePreview?.js || '').trim();
  const hasSitePreview = Boolean(previewHtml || previewCss || previewJs);
  const deck = Array.isArray(slides) ? slides : [];
  const liveBrowserUrl =
    (browserOperatorLiveState?.url && String(browserOperatorLiveState.url).trim()) ||
    (browserPreviewUrl && String(browserPreviewUrl).trim()) ||
    '';
  const toolView = previewKind === 'browser' ? 'browser' : previewKind === 'slides' ? 'slides' : previewKind === 'build' ? 'build' : 'plain';

  useEffect(() => {
    if (previewKind === 'browser') setAddressDraft(liveBrowserUrl);
  }, [previewKind, liveBrowserUrl]);

  useEffect(() => {
    if (previewKind !== 'browser') return;
    const next = String(liveBrowserUrl || '').trim();
    if (!next) return;
    setManualBrowserUrl(next);
    setScreenMode((mode) => (mode === 'settings' ? mode : 'browser'));
  }, [previewKind, liveBrowserUrl]);

  useEffect(() => {
    setScreenMode((mode) => {
      if (mode === 'browser' || mode === 'settings' || mode === 'code') return mode;
      return previewKind === 'video' ? 'video' : null;
    });
    setSiteCopy(null);
    setMenuOpen(false);
    setSettingsOpen(false);
    setMoreOpen(false);
  }, [previewKind]);

  useEffect(() => {
    if (!videoEdit?.id) return;
    setScreenMode('video');
    setMenuOpen(false);
    setSettingsOpen(false);
    setMoreOpen(false);
    setSearchOpen(false);
  }, [videoEdit]);

  useEffect(() => {
    const section = settingsRequest?.section;
    if (!section) return;
    setSettingsSection(section);
    setScreenMode('settings');
    setMenuOpen(false);
    setSettingsOpen(false);
    setMoreOpen(false);
  }, [settingsRequest]);

  useEffect(() => {
    if (siteBuild?.phase === 'thinking') setSiteCopy(null);
  }, [siteBuild?.phase]);

  const buildDoneStamp = siteBuild?.phase === 'done'
    ? (siteBuild.files?.find((file) => file.path === 'system.json')?.content || 'done')
    : '';

  useEffect(() => {
    if (!buildDoneStamp) return;
    setSessionBuilds((count) => count + 1);
    setSlideIndex(0);
    setScreenMode((mode) => (mode === 'browser' || mode === 'settings' || mode === 'video' ? mode : 'preview'));
  }, [buildDoneStamp]);

  useEffect(() => {
    if (!menuOpen && !settingsOpen && !moreOpen) return undefined;
    const onPointer = (event) => {
      if (menuWrapRef.current && !menuWrapRef.current.contains(event.target)) {
        setMenuOpen(false);
        setSettingsOpen(false);
      }
      if (moreWrapRef.current && !moreWrapRef.current.contains(event.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [menuOpen, settingsOpen, moreOpen]);

  useEffect(() => {
    if (previewKind !== 'slides') return;
    setSlideIndex(Math.max(0, deck.length - 1));
  }, [previewKind, deck.length]);

  const slideProgress = String(
    Array.isArray(processLog) ? processLog[processLog.length - 1] : ''
  ).match(/Added slide (\d+) of (\d+)/);
  const slidesStillBuilding = previewKind === 'slides' && (!slideProgress || slideProgress[1] !== slideProgress[2]);
  const running = Boolean(
    (previewKind === 'browser' && browserOperatorLiveState?.running) ||
      (previewKind === 'build' && Boolean(siteBuild?.streaming)) ||
      slidesStillBuilding ||
      (previewKind === 'gmail' && (gmail?.gmailLoading || gmail?.gmailOrganizing)) ||
      (previewKind === 'ideas' && ideaGeneration?.active) ||
      (previewKind === 'camera' && camera?.active) ||
      // Video is idle until the user loads a clip — processLog alone must not mean Working.
      (previewKind !== 'slides' &&
        previewKind !== 'video' &&
        Array.isArray(processLog) &&
        processLog.length > 0 &&
        previewKind !== 'idle' &&
        !hasSitePreview &&
        !hasImage &&
        !hasBrowserShot)
  );

  const busy = Boolean(
    running ||
      (previewKind === 'browser' && browserOperatorLiveState?.running) ||
      (previewKind === 'gmail' && (gmail?.gmailLoading || gmail?.gmailOrganizing)) ||
      (previewKind === 'ideas' && ideaGeneration?.active) ||
      (previewKind === 'camera' && camera?.active)
  );

  const kindLabel = KIND_LABELS[previewKind] || 'Preview';

  const contextLine = useMemo(() => {
    if (previewKind === 'build') return 'Live screen';
    if (previewKind === 'browser') {
      return browserOperatorLiveState?.url || browserPreviewUrl || '';
    }
    if (previewKind === 'slides') {
      const count = Array.isArray(slides) ? slides.length : 0;
      return count ? `${count} slide${count === 1 ? '' : 's'}` : 'Building slides';
    }
    if (previewKind === 'site' || hasSitePreview) return 'Interactive preview';
    if (previewKind === 'image' || (hasImage && previewKind !== 'site')) return 'Result image';
    if (previewKind === 'gmail') return 'Gmail';
    if (previewKind === 'ideas') return 'Idea Generation';
    if (previewKind === 'presets') return 'Prompt Presets';
    if (previewKind === 'camera') return 'Camera';
    if (previewKind === 'video') return 'Video';
    return '';
  }, [previewKind, browserOperatorLiveState?.url, browserPreviewUrl, hasSitePreview, hasImage, slides]);

  const toUrl = (raw) => {
    const text = String(raw || '').trim();
    if (!text) return '';
    if (/^https?:\/\//i.test(text)) {
      return text.replace(/^https?:\/\//i, (scheme) => scheme.toLowerCase());
    }
    if (text.startsWith('//')) return `https:${text}`;
    if (text.includes('.') && !/\s/.test(text)) return `https://${text.replace(/^\/+/, '')}`;
    return `https://www.google.com/search?q=${encodeURIComponent(text)}`;
  };

  const goAddress = () => {
    const url = toUrl(addressDraft);
    if (!url) return;
    setAddressDraft(url);
    if (screenMode === 'browser' || previewKind !== 'browser') {
      setManualBrowserUrl(url);
      setScreenMode('browser');
    }
    browserRef.current?.navigateTo(url);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const url = toUrl(searchDraft);
    if (!url) return;
    setManualBrowserUrl(url);
    setAddressDraft(url);
    setScreenMode('browser');
    setSearchOpen(false);
    setSearchDraft('');
    setMenuOpen(false);
    setSettingsOpen(false);
    setMoreOpen(false);
  };

  const showNotice = (text) => {
    setNotice(text);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(''), 2400);
  };

  const activeSite = siteCopy || siteBuild;
  const siteFiles = Array.isArray(activeSite?.files) ? activeSite.files : [];

  const sharePage = async () => {
    const browserUrl = addressDraft.trim() || manualBrowserUrl.trim();
    const text = showingBrowser && browserUrl ? browserUrl : window.location.href;
    setMoreOpen(false);
    try {
      await navigator.clipboard.writeText(text);
      showNotice('Copied');
    } catch (_) {
      showNotice('Could not copy');
    }
  };

  const downloadSiteZip = async () => {
    setMoreOpen(false);
    if (!siteFiles.length) {
      showNotice('Nothing to download.');
      return;
    }
    const filesByPath = {};
    siteFiles.forEach((file) => {
      filesByPath[file.path] = file.content || file.shown || '';
    });
    await createZipAndDownload('site.zip', filesByPath);
    showNotice('Download started.');
  };

  const makeSiteCopy = () => {
    setMoreOpen(false);
    const raw = siteFiles.find((file) => file.path === 'system.json')?.content;
    if (!raw) {
      showNotice('Nothing to copy.');
      return;
    }
    let source;
    try {
      source = JSON.parse(raw);
    } catch (_) {
      showNotice('Nothing to copy.');
      return;
    }
    const brand = String(source.brand || 'site').trim();
    source.brand = /copy$/i.test(brand) ? brand : `${brand} copy`;
    const files = filesFromSource(source).map((file) => ({ ...file, shown: file.content }));
    setSiteCopy({
      ...(activeSite || {}),
      phase: 'done',
      streaming: false,
      fileNote: 'A copy is ready.',
      thoughts: [],
      files,
      preview: previewFromFiles(files),
      active: files[0]?.path || 'system.json',
    });
    setScreenMode('preview');
    showNotice('Copy ready.');
  };

  const publishPage = () => {
    setMoreOpen(false);
    setMenuOpen(false);
    showNotice('This page is ready to publish.');
  };

  const refreshLiveView = () => {
    const browser = browserRef.current;
    if (browser && typeof browser.reload === 'function') {
      browser.reload();
      return;
    }
    setLiveViewKey((key) => key + 1);
  };

  const askInChat = (note) => {
    showNotice(note);
    const box = document.querySelector('[aria-label="askAnything"]');
    if (box && typeof box.focus === 'function') box.focus();
  };

  const setSettingsField = (key, value) => {
    setSettingsForm((current) => ({ ...current, [key]: value }));
  };

  const deckSource = useMemo(() => {
    if (activeSite?.kind !== 'slides') return null;
    const raw = (activeSite.files || []).find((file) => file.path === 'system.json')?.content || '';
    try {
      const parsed = JSON.parse(raw);
      return parsed && Array.isArray(parsed.slides) && parsed.slides.length ? parsed : null;
    } catch (_) {
      return null;
    }
  }, [activeSite]);
  const buildPhaseNow = activeSite?.phase || (activeSite?.streaming ? 'code' : '');
  const generating = buildPhaseNow === 'thinking' || buildPhaseNow === 'code';
  const showingVideo = screenMode === 'video' || (screenMode == null && previewKind === 'video');
  const showingBrowser = screenMode === 'browser' || (screenMode == null && toolView === 'browser');
  const showingDeck = !generating && !showingVideo && screenMode !== 'code' && screenMode !== 'settings' && screenMode !== 'browser' && Boolean(deckSource?.slides?.length);
  const showingSlides = showingDeck || (screenMode == null && toolView === 'slides' && !generating && !showingVideo);
  const stageLabel = showingVideo ? 'Video' : kindLabel;

  const renderStageBody = () => {
    if (showingVideo) {
      return <ComputerVideoView edit={videoEdit} active={!minimized} />;
    }

    if (screenMode === 'settings') {
      return (
        <ComputerSettings
          section={settingsSection}
          onSection={setSettingsSection}
          form={settingsForm}
          setField={setSettingsField}
          onPublish={publishPage}
          onCopy={makeSiteCopy}
          onAsk={askInChat}
          sessionBuilds={sessionBuilds}
          onOpenChatUsage={onOpenChatUsage}
        />
      );
    }

    if (screenMode === 'browser') {
      const normalized = manualBrowserUrl
        ? (manualBrowserUrl.startsWith('http') ? manualBrowserUrl : `https://${manualBrowserUrl}`)
        : '';
      return (
        <div className="preview-panel-browser-live">
          {normalized ? (
            <SandboxBrowser ref={browserRef} url={normalized} embedded hideControls onUrlChange={setAddressDraft} />
          ) : (
            <div className="preview-panel-empty">
              <span className="preview-panel-empty-kicker">Preview</span>
              <p className="preview-panel-empty-title">New tab</p>
              <p className="preview-panel-empty-hint">Type an address, for example https://example.com.</p>
            </div>
          )}
        </div>
      );
    }

    if (screenMode === 'code') {
      return <CodeExplorer siteBuild={activeSite} />;
    }

    const buildPhase = activeSite?.phase || (activeSite?.streaming ? 'code' : '');
    const writingSite = buildPhase === 'thinking' || buildPhase === 'code';
    if (writingSite && activeSite?.files?.length) {
      return <LiveSiteScreen siteBuild={activeSite} solo="code" />;
    }

    if (!writingSite && activeSite?.kind === 'slides' && deckSource?.slides?.length) {
      return (
        <SlideDeck
          slides={deckSource.slides}
          accent={deckSource.accent}
          gold={deckSource.gold}
          index={slideIndex}
          onIndex={setSlideIndex}
        />
      );
    }

    if ((screenMode === 'preview' || (screenMode == null && toolView === 'build')) && activeSite?.files?.length && activeSite?.kind !== 'slides') {
      return <LiveSiteScreen siteBuild={activeSite} solo="preview" />;
    }

    if (toolView === 'build' && siteBuild?.files?.length && activeSite?.kind !== 'slides') {
      return <LiveSiteScreen siteBuild={activeSite} solo={writingSite ? 'code' : 'preview'} />;
    }

    if (previewKind === 'ideas' && ideaGeneration) {
      return (
        <div className="preview-panel-fill">
          <IdeaGenerationPreview {...ideaGeneration} />
        </div>
      );
    }

    if (previewKind === 'presets' && promptPresets) {
      return (
        <div className="preview-panel-presets">
          <PromptPresets
            onSelectPreset={(prompt) => {
              if (typeof promptPresets.onSelect === 'function') promptPresets.onSelect(prompt);
            }}
          />
        </div>
      );
    }

    if (previewKind === 'camera' && camera) {
      return (
        <div className="preview-panel-camera">
          <video ref={camera.videoRef} autoPlay playsInline className="preview-panel-camera-video" />
          <canvas ref={camera.canvasRef} style={{ display: 'none' }} />
          <div className="preview-panel-camera-controls">
            <button type="button" className="preview-panel-camera-btn primary" onClick={camera.onCapture}>
              Capture
            </button>
            <button type="button" className="preview-panel-camera-btn" onClick={camera.onClose}>
              Close
            </button>
          </div>
        </div>
      );
    }

    if (previewKind === 'gmail' && gmail) {
      return (
        <div className="preview-panel-fill preview-panel-fill--light">
          <GmailPreviewPanel {...gmail} />
        </div>
      );
    }

    if (previewKind === 'slides') {
      return (
        <SlideDeck
          slides={deck}
          accent="#0c3d34"
          index={slideIndex}
          onIndex={setSlideIndex}
        />
      );
    }

    if (previewKind === 'browser') {
      const liveUrl =
        (browserOperatorLiveState?.url && String(browserOperatorLiveState.url).trim()) ||
        (browserPreviewUrl && String(browserPreviewUrl).trim()) ||
        '';
      const normalized = liveUrl
        ? (liveUrl.startsWith('http') ? liveUrl : `https://${liveUrl}`)
        : '';

      return (
        <div className="preview-panel-browser-live">
          {hasBrowserShot ? (
            <div className="preview-panel-browser-shot">
              <img
                src={`data:image/png;base64,${browserOperatorLiveState.screenshotBase64}`}
                alt="Browser preview"
                className="preview-panel-media"
              />
              {normalized ? (
                <a
                  className="preview-panel-browser-open"
                  href={normalized}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {'Open page \u2197'}
                </a>
              ) : null}
            </div>
          ) : normalized ? (
            <SandboxBrowser ref={browserRef} url={normalized} embedded hideControls onUrlChange={setAddressDraft} />
          ) : (
            <div className="preview-panel-empty">
              <span className="preview-panel-empty-kicker">Preview</span>
              <p className="preview-panel-empty-title">
                {busy ? 'Opening a live view\u2026' : 'Waiting for a page'}
              </p>
              <p className="preview-panel-empty-hint">
                {'Ask to open a site (for example \u201Copen booking.com\u201D) and it appears here in this same panel.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    if ((previewKind === 'image' || previewKind === 'video' || hasImage) && hasImage && previewKind !== 'site') {
      const asVideo = previewKind === 'video' || isLikelyVideoUrl(resultImageUrl);
      return (
        <div className="preview-panel-image">
          {asVideo ? (
            <video src={resultImageUrl} controls playsInline autoPlay muted className="preview-panel-media" />
          ) : (
            <img src={resultImageUrl} alt="Generated result" />
          )}
        </div>
      );
    }

    if ((previewKind === 'site' || hasSitePreview) && hasSitePreview) {
      return (
        <LivePreview
          key="site-preview"
          html={previewHtml}
          css={previewCss}
          js={previewJs}
          isVisible
          hideChrome
          enablePreviewEdit={false}
          onRuntimeEvent={(evt) => {
            if (!onComputerRequestFix) return;
            if (evt?.type === 'error' || evt?.type === 'unhandledrejection') {
              onComputerRequestFix({
                lastError: evt,
                errorCount: 1,
                preview: { html: previewHtml, css: previewCss, js: previewJs },
                recentEvents: [evt],
              });
            }
          }}
        />
      );
    }

    if (hasImage) {
      const asVideo = isLikelyVideoUrl(resultImageUrl);
      return (
        <div className="preview-panel-image">
          {asVideo ? (
            <video src={resultImageUrl} controls playsInline className="preview-panel-media" />
          ) : (
            <img src={resultImageUrl} alt="Generated result" />
          )}
        </div>
      );
    }

    return (
      <div className="preview-panel-empty">
        <span className="preview-panel-empty-kicker">Computer</span>
        <p className="preview-panel-empty-title">
          {busy ? 'Working\u2026' : 'The work shows up here'}
        </p>
        <p className="preview-panel-empty-hint">
          Ask Zolaid to open a site or make a PowerPoint. The browser and the slides appear on this screen while it works.
        </p>
      </div>
    );
  };

  const statusTitle = truncate(
    showingVideo ? 'Video editor' : (contextLine || "Zolaid's computer"),
    72
  );

  const panel = (
    <div
      className={[
        'zolaid-workspace',
        'agent-sandbox',
        'preview-panel',
        minimized ? 'minimized' : '',
        size === 'large' ? 'browser-task-panel-size-large' : '',
        isFull ? 'browser-task-panel-full' : '',
        fullscreen ? 'agent-sandbox-fullscreen' : '',
        busy ? 'preview-panel--busy' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      id="zolaid-agent-workspace"
    >
      <div className="zolaid-workspace-inner agent-sandbox-inner">
        <div className={`zolaid-workspace-header preview-panel-header is-${showingBrowser ? 'browser' : showingSlides ? 'slides' : toolView}${generating ? ' is-generating' : ''}`}>
          <div className="preview-panel-header-left">
            <div className="computer-preview-wrap" ref={menuWrapRef}>
              <div className="computer-preview-btn">
                <button
                  type="button"
                  className="computer-preview-label"
                  onClick={() => {
                    setScreenMode('preview');
                    setMenuOpen(false);
                    setSettingsOpen(false);
                    setMoreOpen(false);
                  }}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="computer-preview-chevron"
                  aria-label="Preview menu"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-controls="computer-preview-menu"
                  onClick={() => {
                    setMenuOpen((open) => !open);
                    setSettingsOpen(false);
                    setMoreOpen(false);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                    <path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M4 6.5 8 10.5 12 6.5" />
                  </svg>
                </button>
              </div>
              {menuOpen ? (
                <div className="computer-preview-menu" id="computer-preview-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setScreenMode('code');
                      setMenuOpen(false);
                      setSettingsOpen(false);
                    }}
                  >
                    Code
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setScreenMode('settings');
                      setMenuOpen(false);
                      setSettingsOpen(false);
                    }}
                  >
                    Setting
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setScreenMode('browser');
                      setManualBrowserUrl('');
                      setAddressDraft('');
                      setSearchOpen(false);
                      setMenuOpen(false);
                      setSettingsOpen(false);
                    }}
                  >
                    New tab
                  </button>
                </div>
              ) : null}
            </div>
            {searchOpen ? (
              <form className="computer-search-form" onSubmit={submitSearch}>
                <input
                  className="computer-search-field"
                  aria-label="Search or enter a URL"
                  placeholder="https://example.com"
                  value={searchDraft}
                  autoFocus
                  spellCheck={false}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') submitSearch(event);
                  }}
                />
              </form>
            ) : (
              <button
                type="button"
                className="zolaid-workspace-header-btn"
                title="Search"
                aria-label="Search"
                onClick={() => {
                  setSearchOpen(true);
                  setMenuOpen(false);
                  setSettingsOpen(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
              </button>
            )}
            <div className="computer-more-wrap" ref={moreWrapRef}>
              <button
                type="button"
                className="computer-more-btn"
                aria-label="More"
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                onClick={() => {
                  setMoreOpen((open) => !open);
                  setMenuOpen(false);
                  setSettingsOpen(false);
                }}
              >
                ...
              </button>
              {moreOpen ? (
                <div className="computer-preview-menu" role="menu" aria-label="More">
                  <button type="button" role="menuitem" onClick={sharePage}>Share</button>
                  <button type="button" role="menuitem" onClick={downloadSiteZip}>
                    {siteFiles.length ? 'Download as zip' : 'Nothing to download'}
                  </button>
                  <button type="button" role="menuitem" onClick={makeSiteCopy}>Make a copy</button>
                </div>
              ) : null}
            </div>
            <button type="button" className="computer-publish-btn" onClick={publishPage}>
              <span aria-hidden="true">{'\uD83D\uDE80'}</span>
              Publish
            </button>
            {notice ? <span className="computer-notice" role="status">{notice}</span> : null}
            {showingBrowser ? (
              <div className="computer-browser-bar">
                <button type="button" className="zolaid-workspace-header-btn" title="Back" aria-label="Back" onClick={() => browserRef.current?.back()}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
                </button>
                <button type="button" className="zolaid-workspace-header-btn" title="Forward" aria-label="Forward" onClick={() => browserRef.current?.forward()}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                </button>
                <button type="button" className="zolaid-workspace-header-btn" title="Reload" aria-label="Reload" onClick={() => browserRef.current?.reload()}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                </button>
                <input
                  className="computer-address"
                  aria-label="Address"
                  value={addressDraft}
                  placeholder="Search or enter a URL"
                  spellCheck={false}
                  onChange={(event) => setAddressDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      goAddress();
                    }
                  }}
                />
                <button type="button" className="zolaid-workspace-header-btn computer-go" title="Go" aria-label="Go" onClick={goAddress}>
                  Go
                </button>
              </div>
            ) : null}
            {showingSlides ? (
              <div className="computer-slide-controls">
                <button
                  type="button"
                  className="zolaid-workspace-header-btn"
                  title="Previous slide"
                  aria-label="Previous slide"
                  disabled={slideIndex <= 0}
                  onClick={() => setSlideIndex((index) => Math.max(0, index - 1))}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
                </button>
                <span className="computer-slide-count">{(deckSource?.slides || deck).length ? `${Math.min(slideIndex, (deckSource?.slides || deck).length - 1) + 1} / ${(deckSource?.slides || deck).length}` : '0 / 0'}</span>
                <button
                  type="button"
                  className="zolaid-workspace-header-btn"
                  title="Next slide"
                  aria-label="Next slide"
                  disabled={!(deckSource?.slides || deck).length || slideIndex >= (deckSource?.slides || deck).length - 1}
                  onClick={() => setSlideIndex((index) => Math.min((deckSource?.slides || deck).length - 1, index + 1))}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                </button>
              </div>
            ) : null}
            {!showingBrowser && !showingSlides ? (
              <>
                <span className="preview-panel-kind">{stageLabel}</span>
                {busy ? <span className="preview-panel-live" aria-label="In progress">Live</span> : null}
              </>
            ) : null}
          </div>
          <div className="zolaid-workspace-header-actions">
            <button
              type="button"
              className={`zolaid-workspace-header-btn computer-video-btn${showingVideo ? ' is-active' : ''}`}
              title="Video"
              aria-label="Video"
              aria-pressed={showingVideo}
              onClick={() => {
                if (minimized && typeof onMinimize === 'function') onMinimize();
                setScreenMode('video');
                setMenuOpen(false);
                setSettingsOpen(false);
                setMoreOpen(false);
                setSearchOpen(false);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="6" width="12" height="12" rx="2" />
                <path d="m15 10 6-3v10l-6-3" />
              </svg>
            </button>
            <button
              type="button"
              className="zolaid-workspace-header-btn computer-refresh-btn"
              title="Refresh"
              aria-label="Refresh"
              onClick={refreshLiveView}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
            <button
              type="button"
              className="zolaid-workspace-header-btn computer-minimize-btn"
              title={minimized ? 'Restore' : 'Minimize'}
              aria-label={minimized ? 'Restore Computer' : 'Minimize Computer'}
              aria-pressed={minimized}
              onClick={() => {
                if (fullscreen && typeof onToggleFullscreen === 'function') onToggleFullscreen();
                if (typeof onMinimize === 'function') onMinimize();
              }}
            >
              {minimized ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <rect x="2.75" y="2.75" width="10.5" height="10.5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3.25 12.25h9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className={`zolaid-workspace-header-btn computer-fullscreen-btn${fullscreen ? ' is-active' : ''}`}
              title={fullscreen ? 'Exit full screen' : 'Full screen'}
              aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
              aria-pressed={fullscreen}
              onClick={() => {
                if (minimized && typeof onMinimize === 'function') onMinimize();
                if (typeof onToggleFullscreen === 'function') onToggleFullscreen();
              }}
            >
              {fullscreen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 4v5H4" />
                  <path d="M15 4v5h5" />
                  <path d="M9 20v-5H4" />
                  <path d="M15 20v-5h5" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 9V4h5" />
                  <path d="M20 9V4h-5" />
                  <path d="M4 15v5h5" />
                  <path d="M20 15v5h-5" />
                </svg>
              )}
            </button>
            <button type="button" className="zolaid-workspace-header-btn" title="Close" aria-label="Close" onClick={onClose}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>

        {!minimized && (
          <>
            <div className="preview-panel-stage" key={liveViewKey}>
              {renderStageBody()}
              {busy && (
                <div className="preview-panel-activity" aria-hidden="true">
                  <span className="preview-panel-activity-bar" />
                </div>
              )}
            </div>

            <div className="zolaid-workspace-taskbar preview-panel-taskbar">
              <div className="zolaid-workspace-taskbar-content">
                <div className="zolaid-workspace-taskbar-inner">
                  <span className={`preview-panel-status-pip${busy ? ' is-on' : ''}`} aria-hidden />
                  <div className="zolaid-workspace-taskbar-info">
                    <div
                      className={`zolaid-workspace-taskbar-title ${!contextLine ? 'waiting' : ''}`}
                      title={statusTitle}
                    >
                      {statusTitle}
                    </div>
                    <div className="zolaid-workspace-taskbar-subtitle">
                      {busy ? 'Working' : stageLabel}
                    </div>
                  </div>
                </div>
              </div>
              <div className="zolaid-workspace-taskbar-steps" title="Status updates">
                {processLog.length || '0'}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (fullscreen && !minimized && typeof document !== 'undefined') {
    return createPortal(panel, document.body);
  }
  return panel;
}
